import React, {useCallback, useMemo} from 'react';
import Select, {createFilter} from 'react-select';
import {ChartFilterType} from '@/views/storyboard/utils/chartConstants';
import i18n from 'i18n';

export interface FilterDropdownOption {
    value: string;
    label: string;
    description: string;
    type: ChartFilterType;
    isPriority?: boolean;
}

export interface FilterDropdownGroup {
    key: string;
    label: string;
    options: FilterDropdownOption[];
}

export type ChartSettingsFilterOption = FilterDropdownOption;
export type ChartSettingsFilterGroup = FilterDropdownGroup;

const FILTER_TYPE_LABELS: Partial<Record<ChartFilterType, string>> = {
    [ChartFilterType.SelectionField]: 'chart_settings_filter_type_selection.text',
    [ChartFilterType.ComputedClassification]: 'chart_settings_filter_type_classification.text',
    [ChartFilterType.NumericalDescriptor]: 'chart_settings_filter_type_numeric.text',
    [ChartFilterType.FeedbackNumericalDescriptor]: 'chart_settings_filter_type_numeric.text',
    [ChartFilterType.TextSample]: 'chart_settings_filter_type_text.text',
    [ChartFilterType.FeedbackTextSample]: 'chart_settings_filter_type_text.text',
    [ChartFilterType.SummaryTextSample]: 'chart_settings_filter_type_summary.text',
};

interface Props {
    groups: FilterDropdownGroup[];
    onSelect: (option: FilterDropdownOption) => void;
    isLoading: boolean;
    parent: string;
}

const ChartFilterDropdown = React.memo<Props>(({
    groups,
    onSelect,
    isLoading,
    parent,
}) => {
    const groupedOptions = useMemo(
        () => groups.map((group) => ({
            label: group.label,
            options: group.options,
        })),
        [groups],
    );

    const getFilterTypeLabel = useCallback((option: FilterDropdownOption) => (
        FILTER_TYPE_LABELS[option.type]
            ? i18n.t(FILTER_TYPE_LABELS[option.type]!)
            : option.type
    ), []);

    const handleSelect = useCallback((option: FilterDropdownOption | null) => {
        if (!option) {
            return;
        }

        onSelect(option);
    }, [onSelect]);

    return (
        <div className="cs-filter-dropdown-select">
            <Select<FilterDropdownOption, false>
                inputId={`${parent}-chart-settings-filter-dropdown`}
                name="chart-settings-filter-dropdown"
                value={null}
                onChange={handleSelect}
                options={groupedOptions}
                placeholder={i18n.t('cs_add_filter.text')}
                isClearable={false}
                isSearchable
                isDisabled={isLoading}
                isLoading={isLoading}
                loadingMessage={() => i18n.t('jt_loading_opt.text')}
                noOptionsMessage={() => i18n.t('jt_no_results_found.text')}
                maxMenuHeight={236}
                menuPlacement="auto"
                menuShouldScrollIntoView
                filterOption={createFilter({
                    matchFrom: 'any',
                    stringify: (candidate) => {
                        const option = candidate.data as FilterDropdownOption;
                        return `${option.label} ${option.description} ${getFilterTypeLabel(option)}`;
                    },
                })}
                formatGroupLabel={(group) => (
                    <div className="cs-filter-dropdown__group-label">
                        {group.label}
                    </div>
                )}
                formatOptionLabel={(option) => (
                    <div className="cs-filter-dropdown__option">
                        <span className="cs-filter-dropdown__option-row">
                            <span className="cs-filter-dropdown__option-name">
                                {option.label}
                            </span>
                            <span className="cs-filter-dropdown__option-desc">
                                {option.description}
                            </span>
                        </span>
                        <span className="cs-filter-dropdown__option-type">
                            {getFilterTypeLabel(option)}
                        </span>
                    </div>
                )}
                styles={{
                    control: (base) => ({...base, minHeight: 34, height: 34}),
                    valueContainer: (base) => ({...base, height: 34, padding: '0 14px'}),
                    placeholder: (base) => ({...base, color: '#9099A4'}),
                    input: (base) => ({...base, margin: 0, padding: 0}),
                    indicatorsContainer: (base) => ({
                        ...base,
                        height: 34,
                        alignSelf: 'center',
                    }),
                    menu: (base) => ({...base, marginBottom: 8}),
                    menuList: (base) => ({...base, maxHeight: 236, padding: '0 6px 6px'}),
                    group: (base) => ({...base, paddingBottom: 0, paddingTop: 2}),
                    groupHeading: (base) => ({...base, marginBottom: 0, padding: 0}),
                    option: (base, state) => ({
                        ...base,
                        padding: 0,
                        background: state.isFocused ? 'rgba(0, 158, 251, 0.08)' : 'transparent',
                        backgroundColor: state.isFocused ? 'rgba(0, 158, 251, 0.08)' : 'transparent',
                        color: 'inherit',
                        cursor: 'pointer',
                        ':active': {
                            ...base[':active'],
                            backgroundColor: 'rgba(0, 158, 251, 0.08)',
                        },
                    }),
                }}
            />
        </div>
    );
});

export default ChartFilterDropdown;
